/**
 * Cadastros > Configuração da Funded (spec 065): o administrador muda o valor padrao e os prazos da taxa de manutencao
 * (modulo `assinatura`, 2.1 e 2.2) e os prazos do pedido de aprovacao do Challenge (modulo `nelogica-v2`, 2.3). Cada
 * secao so aparece com o modulo dela. Ao lado de cada campo, o numero do regulamento e os limites, que vem da API; o
 * rotulo dos prazos ("2 dias úteis") tambem vem dela. Salvar pede confirmacao com o antes e o depois.
 *
 * Mudar vale pro que nasce depois (2.4): parcela, ciclo aberto, pedido e suspensao ja existentes guardam o que tinham.
 * Quem garante e o backend; a tela so avisa. Ler e de todo administrador; salvar, de ADMIN (o backend responde 403).
 */
import { Aviso, Botao, CabecalhoDePagina, Campo, CampoDinheiro, CampoMudado, Carregando, Grade, ListaDeMudancas, Pilha, Secao, formatarDataHora, formatarDolares, soDigitos, type MudancaSalva } from "@mtc/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState, type ReactNode } from "react";
import { erroDe, quem, useApi, usePapel, type ConfiguracaoDaManutencao, type PrazosDaAprovacao } from "../api";
import { useAmbiente } from "../contexto";
import { DialogoDeAcao } from "./DialogoDeAcao";

type Unidade = "dia" | "hora";
type Limite = [number, number];

/** "1 dia", "30 dias", "1 hora útil", "48 horas úteis". */
const emUnidade = (n: number, u: Unidade) => (u === "dia" ? `${n} ${n === 1 ? "dia" : "dias"}` : `${n} ${n === 1 ? "hora útil" : "horas úteis"}`);

/** O nome de quem mudou por ultimo: o administrador pelo id da trilha ("operador:abc"); sem o nome, "Administrador". */
function useQuemMudou() {
  const { ler } = useApi();
  const nomes = useQuery({ queryKey: ["nomes-dos-administradores"], queryFn: () => ler<{ itens: { id: string; nome: string }[] }>("/v1/admin/administradores/nomes"), staleTime: 5 * 60_000 });
  return (ator: string | null) => (ator?.startsWith("operador:") ? nomes.data?.itens.find((o) => `operador:${o.id}` === ator)?.nome ?? "Administrador" : quem(ator));
}

function UltimaMudanca({ em, por }: { em: string | null; por: string | null }) {
  const quemMudou = useQuemMudou();
  return (
    <p className="mtc-texto-2" style={{ margin: 0, fontSize: "var(--t-sm)" }}>
      {em ? <>Última mudança em {formatarDataHora(em)}, por {quemMudou(por)}.</> : "Ninguém mudou ainda: valem os números do regulamento."}
    </p>
  );
}

/** Um numero inteiro (dias ou horas) com o de antes marcado, o do regulamento e os limites na dica. */
function CampoInteiro({ rotulo, valor, aoMudar, salvo, padrao, limite, unidade, explica, disabled }: { rotulo: string; valor: string; aoMudar: (v: string) => void; salvo: number; padrao: number; limite: Limite; unidade: Unidade; explica: ReactNode; disabled?: boolean }) {
  return (
    <CampoMudado mudou={valor !== String(salvo)} antes={emUnidade(salvo, unidade)}>
      <Campo
        rotulo={rotulo}
        valor={valor}
        aoMudar={(v) => aoMudar(soDigitos(v).slice(0, 3))}
        inputMode="numeric"
        mono
        disabled={disabled}
        erro={erroDoInteiro(valor, limite)}
        dica={<>{explica} Regulamento: {emUnidade(padrao, unidade)}. De {limite[0]} a {limite[1]}.</>}
      />
    </CampoMudado>
  );
}

const erroDoInteiro = (valor: string, [min, max]: Limite) => (valor === "" ? "Preencha com um número." : Number(valor) < min || Number(valor) > max ? `Use de ${min} a ${max}.` : undefined);

/** Os botoes do fim de cada secao: desfazer o que nao foi salvo, voltar ao regulamento e salvar (com confirmacao). */
function Rodape({ mudou, igualAoRegulamento, valido, admin, aoDesfazer, aoRegulamento, aoSalvar, botao }: { mudou: boolean; igualAoRegulamento: boolean; valido: boolean; admin: boolean; aoDesfazer: () => void; aoRegulamento: () => void; aoSalvar: () => void; botao: string }) {
  if (!admin) return null;
  return (
    <div className="mtc-linha" style={{ gap: "var(--e-2)", justifyContent: "flex-end", flexWrap: "wrap" }}>
      {mudou && <Botao variante="fantasma" onClick={aoDesfazer}>Desfazer</Botao>}
      {!igualAoRegulamento && <Botao variante="secundario" onClick={aoRegulamento}>Usar os números do regulamento</Botao>}
      <Botao disabled={!mudou || !valido} onClick={aoSalvar}>{botao}</Botao>
    </div>
  );
}

export function PaginaConfiguracaoDaFunded() {
  const { modulos } = useAmbiente();
  const { pode } = usePapel();
  const admin = pode("ADMIN");
  const [feito, setFeito] = useState<string>();
  const concluir = (t: string) => { setFeito(t); window.scrollTo({ top: 0, behavior: "smooth" }); };
  return (
    <Pilha espaco={5}>
      <CabecalhoDePagina titulo="Configuração da Funded" descricao="O valor e os prazos da taxa de manutenção e os prazos do pedido de aprovação do Challenge. Os números de partida são os do regulamento; o valor de cada plano fica em Planos." />
      {feito && <Aviso tipo="sucesso">{feito}</Aviso>}
      {!admin && <Aviso tipo="info">Mudar é do administrador. Você vê o que vale agora.</Aviso>}
      <Aviso tipo="info">
        <strong>Vale pro que nascer depois de salvar.</strong> O que já foi combinado não muda: a parcela que já nasceu guarda o valor, o ciclo aberto guarda o tamanho dele, o pedido já feito ou aprovado guarda os prazos e o plano já suspenso guarda o último dia pra pagar.
      </Aviso>
      {modulos.includes("assinatura") && <CobrancaDaManutencao admin={admin} aoConcluir={concluir} />}
      {modulos.includes("nelogica-v2") && <PrazosDoPedido admin={admin} aoConcluir={concluir} />}
    </Pilha>
  );
}

// ---------------------------------------------------------------- taxa de manutencao

const CHAVE_DA_MANUTENCAO = ["configuracao-da-funded", "manutencao"];

function CobrancaDaManutencao({ admin, aoConcluir }: { admin: boolean; aoConcluir: (t: string) => void }) {
  const { ler } = useApi();
  const q = useQuery({ queryKey: CHAVE_DA_MANUTENCAO, queryFn: () => ler<ConfiguracaoDaManutencao>("/v1/admin/funded/manutencao") });
  return (
    <Secao rotulo="Taxa de manutenção" titulo="Cobrança da taxa de manutenção">
      {q.isLoading ? <Carregando compacto texto="Buscando a configuração" /> : !q.data ? <Aviso tipo="erro">{erroDe(q.error)}</Aviso> : <FormularioDaManutencao key={q.data.atualizado_em ?? "regulamento"} c={q.data} admin={admin} aoConcluir={aoConcluir} />}
    </Secao>
  );
}

type Manutencao = { valor: number; nasce: string; ciclo: string; regularizar: string };
const daManutencao = (c: Pick<ConfiguracaoDaManutencao, "valor_usd_centavos" | "nasce_dias_antes" | "ciclo_dias" | "regularizar_dias">): Manutencao => ({ valor: c.valor_usd_centavos, nasce: String(c.nasce_dias_antes), ciclo: String(c.ciclo_dias), regularizar: String(c.regularizar_dias) });
const igualManutencao = (a: Manutencao, b: Manutencao) => a.valor === b.valor && a.nasce === b.nasce && a.ciclo === b.ciclo && a.regularizar === b.regularizar;

function FormularioDaManutencao({ c, admin, aoConcluir }: { c: ConfiguracaoDaManutencao; admin: boolean; aoConcluir: (t: string) => void }) {
  const { por } = useApi();
  const qc = useQueryClient();
  const salvo = daManutencao(c);
  const regulamento = daManutencao(c.padrao);
  const [f, setF] = useState(salvo);
  const [confirmar, setConfirmar] = useState(false);
  const resposta = useRef<ConfiguracaoDaManutencao | null>(null);
  const mudar = (campo: keyof Manutencao) => (v: string | number) => setF((x) => ({ ...x, [campo]: v }));
  const [vMin, vMax] = c.limites.valor_usd_centavos;
  const erroDoValor = f.valor < vMin || f.valor > vMax ? `Use de ${formatarDolares(vMin)} a ${formatarDolares(vMax)}.` : undefined;
  const valido = !erroDoValor && !erroDoInteiro(f.nasce, c.limites.nasce_dias_antes) && !erroDoInteiro(f.ciclo, c.limites.ciclo_dias) && !erroDoInteiro(f.regularizar, c.limites.regularizar_dias);
  const mudou = !igualManutencao(f, salvo);
  const dias = (s: string) => emUnidade(Number(s), "dia");
  const mudancas: MudancaSalva[] = [
    { campo: "valor", rotulo: "Valor padrão", antes: formatarDolares(salvo.valor), depois: formatarDolares(f.valor) },
    { campo: "nasce", rotulo: "A parcela nasce", antes: `${dias(salvo.nasce)} antes`, depois: `${dias(f.nasce)} antes` },
    { campo: "ciclo", rotulo: "Ciclo", antes: dias(salvo.ciclo), depois: dias(f.ciclo) },
    { campo: "regularizar", rotulo: "Pra regularizar", antes: dias(salvo.regularizar), depois: dias(f.regularizar) },
  ].filter((m) => m.antes !== m.depois);
  return (
    <>
      <UltimaMudanca em={c.atualizado_em} por={c.atualizado_por} />
      <Grade minimo={280}>
        <CampoMudado mudou={f.valor !== salvo.valor} antes={formatarDolares(salvo.valor)}>
          <CampoDinheiro
            rotulo="Valor padrão"
            moeda="USD"
            centavos={f.valor}
            aoMudar={mudar("valor")}
            disabled={!admin}
            erro={erroDoValor}
            dica={<>Por ciclo, em dólar. Vale pros planos sem valor próprio em Planos. Regulamento: {formatarDolares(c.padrao.valor_usd_centavos)}. De {formatarDolares(vMin)} a {formatarDolares(vMax)}.</>}
          />
        </CampoMudado>
        <CampoInteiro rotulo="Dias antes do vencimento" valor={f.nasce} aoMudar={mudar("nasce")} salvo={c.nasce_dias_antes} padrao={c.padrao.nasce_dias_antes} limite={c.limites.nasce_dias_antes} unidade="dia" disabled={!admin} explica="Quando a parcela nasce e a cotação do dólar é lida." />
        <CampoInteiro rotulo="Tamanho do ciclo" valor={f.ciclo} aoMudar={mudar("ciclo")} salvo={c.ciclo_dias} padrao={c.padrao.ciclo_dias} limite={c.limites.ciclo_dias} unidade="dia" disabled={!admin} explica="Dias de um vencimento ao próximo." />
        <CampoInteiro rotulo="Dias pra regularizar" valor={f.regularizar} aoMudar={mudar("regularizar")} salvo={c.regularizar_dias} padrao={c.padrao.regularizar_dias} limite={c.limites.regularizar_dias} unidade="dia" disabled={!admin} explica="Dias corridos pra pagar depois da suspensão; passou, o cancelamento vai pra sua confirmação." />
      </Grade>
      <Rodape mudou={mudou} igualAoRegulamento={igualManutencao(f, regulamento)} valido={valido} admin={admin} aoDesfazer={() => setF(salvo)} aoRegulamento={() => setF(regulamento)} aoSalvar={() => setConfirmar(true)} botao="Salvar a cobrança" />
      <DialogoDeAcao
        aberto={confirmar}
        aoFechar={() => setConfirmar(false)}
        titulo="Salvar a cobrança da taxa de manutenção"
        descricao="Vale pras parcelas, ciclos e suspensões que nascerem depois de salvar. Parcela que já nasceu, ciclo aberto e plano já suspenso ficam como estão."
        botao="Salvar"
        aoEnviar={async () => {
          resposta.current = await por<ConfiguracaoDaManutencao>("/v1/admin/funded/manutencao", { valor_usd_centavos: f.valor, nasce_dias_antes: Number(f.nasce), ciclo_dias: Number(f.ciclo), regularizar_dias: Number(f.regularizar) });
        }}
        aoConcluir={() => {
          // Depois do dialogo fechar: a resposta nova troca a chave do formulario, que recomeca com o que foi salvo.
          if (resposta.current) qc.setQueryData(CHAVE_DA_MANUTENCAO, resposta.current);
          // O padrao aparece em Planos e o texto da cobranca na ficha e na lista: tudo le de novo.
          void qc.invalidateQueries({ queryKey: ["taxas-de-manutencao"] });
          void qc.invalidateQueries({ queryKey: ["mensalidades"] });
          aoConcluir("Cobrança da taxa de manutenção salva. Vale pro que nascer daqui em diante.");
        }}
      >
        <ListaDeMudancas itens={mudancas} />
      </DialogoDeAcao>
    </>
  );
}

// ---------------------------------------------------------------- pedido de aprovacao

const CHAVE_DA_APROVACAO = ["configuracao-da-funded", "aprovacao"];

function PrazosDoPedido({ admin, aoConcluir }: { admin: boolean; aoConcluir: (t: string) => void }) {
  const { ler } = useApi();
  const q = useQuery({ queryKey: CHAVE_DA_APROVACAO, queryFn: () => ler<PrazosDaAprovacao>("/v1/admin/funded/aprovacao") });
  return (
    <Secao rotulo="Pedido de aprovação" titulo="Prazos do pedido de aprovação do Challenge">
      {q.isLoading ? <Carregando compacto texto="Buscando os prazos" /> : !q.data ? <Aviso tipo="erro">{erroDe(q.error)}</Aviso> : <FormularioDosPrazos key={q.data.atualizado_em ?? "regulamento"} p={q.data} admin={admin} aoConcluir={aoConcluir} />}
    </Secao>
  );
}

function FormularioDosPrazos({ p, admin, aoConcluir }: { p: PrazosDaAprovacao; admin: boolean; aoConcluir: (t: string) => void }) {
  const { por } = useApi();
  const qc = useQueryClient();
  const salvo = { analise: String(p.analise_horas), liberacao: String(p.liberacao_horas) };
  const regulamento = { analise: String(p.padrao.analise_horas), liberacao: String(p.padrao.liberacao_horas) };
  const [f, setF] = useState(salvo);
  const [confirmar, setConfirmar] = useState(false);
  const resposta = useRef<PrazosDaAprovacao | null>(null);
  const igual = (a: typeof salvo, b: typeof salvo) => a.analise === b.analise && a.liberacao === b.liberacao;
  const valido = !erroDoInteiro(f.analise, p.limites.analise_horas) && !erroDoInteiro(f.liberacao, p.limites.liberacao_horas);
  const horas = (s: string) => emUnidade(Number(s), "hora");
  const mudancas: MudancaSalva[] = [
    { campo: "analise", rotulo: "Análise", antes: horas(salvo.analise), depois: horas(f.analise) },
    { campo: "liberacao", rotulo: "Liberação", antes: horas(salvo.liberacao), depois: horas(f.liberacao) },
  ].filter((m) => m.antes !== m.depois);
  return (
    <>
      <UltimaMudanca em={p.atualizado_em} por={p.atualizado_por} />
      <Grade minimo={280}>
        <CampoInteiro rotulo="Horas úteis da análise" valor={f.analise} aoMudar={(v) => setF((x) => ({ ...x, analise: v }))} salvo={p.analise_horas} padrao={p.padrao.analise_horas} limite={p.limites.analise_horas} unidade="hora" disabled={!admin} explica={<>Quanto a equipe tem pra aprovar ou recusar; passou, vira alerta. Hoje o cliente lê “{p.analise_rotulo}”.</>} />
        <CampoInteiro rotulo="Horas úteis da liberação" valor={f.liberacao} aoMudar={(v) => setF((x) => ({ ...x, liberacao: v }))} salvo={p.liberacao_horas} padrao={p.padrao.liberacao_horas} limite={p.limites.liberacao_horas} unidade="hora" disabled={!admin} explica={<>Da aprovação (ou do pagamento da taxa de ativação) até a conta Funded pronta. Hoje o cliente lê “{p.liberacao_rotulo}”.</>} />
      </Grade>
      <Rodape mudou={!igual(f, salvo)} igualAoRegulamento={igual(f, regulamento)} valido={valido} admin={admin} aoDesfazer={() => setF(salvo)} aoRegulamento={() => setF(regulamento)} aoSalvar={() => setConfirmar(true)} botao="Salvar os prazos" />
      <DialogoDeAcao
        aberto={confirmar}
        aoFechar={() => setConfirmar(false)}
        titulo="Salvar os prazos do pedido de aprovação"
        descricao="Vale pro próximo pedido e pra próxima aprovação, na área e no e-mail. O pedido já feito ou aprovado fica com os prazos que tinha."
        botao="Salvar"
        aoEnviar={async () => {
          resposta.current = await por<PrazosDaAprovacao>("/v1/admin/funded/aprovacao", { analise_horas: Number(f.analise), liberacao_horas: Number(f.liberacao) });
        }}
        aoConcluir={() => {
          const r = resposta.current;
          if (!r) return;
          qc.setQueryData(CHAVE_DA_APROVACAO, r);
          aoConcluir(`Prazos do pedido salvos: análise em ${r.analise_rotulo} e liberação em ${r.liberacao_rotulo}.`);
        }}
      >
        <ListaDeMudancas itens={mudancas} />
      </DialogoDeAcao>
    </>
  );
}
